import type { FC, PropsWithChildren } from 'react'

import styled from '@emotion/styled'

import ColumnTrack from '@segment/matcha/components/ColumnTrack'

import FigureBase from '@segment/matcha/components/Figure'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

import BadgeIso27001Svg from './assets/BadgeIso27001.svg'
import BadgeIso27017Svg from './assets/BadgeIso27017.svg'
import BadgeIso27018Svg from './assets/BadgeIso27018.svg'
import BadgeAicpaSocSvg from './assets/BadgeAicpaSoc.svg'
import BadgeCsaSvg from './assets/BadgeCsa.svg'

const copy = {
  overline: `Security Highlight`,
  heading: `Industry-leading Security & Compliance`,
  description: `Our commitment to security and compliance includes a comprehensive suite of certifications and attestations so you can trust that your data stays secure and compliant.`,
}
const link = {
  text: `Learn more`,
  url: `/security/`,
}

const SecurityHighlight: FC<PropsWithChildren<unknown>> = () => {
  const pageConfig = usePageConfig()

  return (
    <Wrapper>
      <SectionBase variant="centered">
        <Section.Copy>
          <CopyTextBase centered={true}>
            <CopyText.Overline>{copy.overline}</CopyText.Overline>
            <CopyText.Heading>{copy.heading}</CopyText.Heading>
            <CopyText.Description>{copy.description}</CopyText.Description>
            <CopyText.Link href={getAbsoluteUrl(link.url, pageConfig)} target="_blank">
              {link.text}
            </CopyText.Link>
          </CopyTextBase>
        </Section.Copy>
        <StyledSectionContents>
          <StyledColumnTrack center={true}>
            <StyledFigureBase>
              <BadgeIso27001Svg />
            </StyledFigureBase>
            <StyledFigureBase>
              <BadgeIso27017Svg />
            </StyledFigureBase>
            <StyledFigureBase>
              <BadgeIso27018Svg />
            </StyledFigureBase>
            <StyledFigureBase>
              <BadgeCsaSvg />
            </StyledFigureBase>
            <StyledFigureBase>
              <BadgeAicpaSocSvg />
            </StyledFigureBase>
          </StyledColumnTrack>
        </StyledSectionContents>
      </SectionBase>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  ${({ theme }) => theme.breakpoints.down(`md`)} {
    max-width: 720px;
    margin: auto;
  }
`

const StyledSectionContents = styled(Section.Contents)`
  max-width: 600px;
  margin: 0 auto;
`

const StyledColumnTrack = styled(ColumnTrack)`
  row-gap: 50px;
  column-gap: 80px;
  grid-template-columns: repeat(2, 1fr);

  ${({ theme }) => theme.breakpoints.up(`sm`)} {
    grid-template-columns: repeat(6, 1fr);
    grid-template-rows: 50% 50%;
  }
`

const StyledFigureBase = styled(FigureBase)`
  justify-content: center;

  &:nth-of-type(4) {
    order: 1;
    grid-column: span 2;
  }

  ${({ theme }) => theme.breakpoints.up(`sm`)} {
    grid-column: span 2;

    &:nth-of-type(4) {
      order: initial;
      grid-column: 2 / span 2;
    }
  }
`

export default SecurityHighlight
